import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getSportEmoji } from '../../utils/sportIcons';
import { dayLabel, groupByDay, inProgressLine, metaLine, roleLabel, timeLabel } from './scheduleHelpers';
import './MySchedule.css';

/**
 * Dashboard "My schedule" (PR 7a): the next 2 weeks of what the player takes
 * part in, live first, grouped by day; then their leagues and tournaments in
 * progress. `schedule` is the GET /users/me/schedule payload.
 *
 * On the dashboard it is two lines so every tile fits on screen without
 * scrolling: the next PREVIEW_ITEMS events, one line each ("Tomorrow 6:45 PM"),
 * or, with nothing upcoming, the first league/tournament in progress; and a
 * "See all" link to the full list on its own page (/schedule, `full`).
 */
export const PREVIEW_ITEMS = 2;
export const PREVIEW_ENTRIES = 1;

const MySchedule = ({ schedule, full = false }) => {
  const navigate = useNavigate();
  const allItems = schedule?.items || [];
  const allInProgress = schedule?.inProgress || [];
  const items = full ? allItems : allItems.slice(0, PREVIEW_ITEMS);
  // Compact: leagues in progress only fill in when nothing is coming up
  const inProgress = full ? allInProgress : (items.length === 0 ? allInProgress.slice(0, PREVIEW_ENTRIES) : []);
  const hidden = Math.max(0, allItems.length - items.length) + Math.max(0, allInProgress.length - inProgress.length);
  const groups = groupByDay(items);
  const days = Number(schedule?.days) || 14;
  const span = days === 14 ? '2 weeks' : `${days} days`;
  const hasAny = allItems.length > 0 || allInProgress.length > 0;

  return (
    <section className={`my-schedule${full ? '' : ' my-schedule--compact'}`} aria-label="My schedule">
      {!full && (
        <div className="my-schedule__head">
          <h2 className="my-schedule__title">My schedule</h2>
          {hasAny && (
            <button type="button" className="my-schedule__more" onClick={() => navigate('/schedule')}>
              {hidden > 0 ? `See all (${hidden} more) →` : 'See all →'}
            </button>
          )}
        </div>
      )}

      {!full && items.length > 0 ? (
        <ul className="my-schedule__list">
          {items.map((item) => {
            const role = roleLabel(item.role);
            return (
              <li key={`${item.kind}-${item.id}`}>
                <button type="button" className="my-schedule__row" onClick={() => navigate(item.link)}>
                  <span className="my-schedule__time">
                    {item.live
                      ? <span className="my-schedule__live">LIVE</span>
                      : <>{dayLabel(item.startsAt, new Date(), item.timeZone)} {timeLabel(item.startsAt, item.timeZone)}</>}
                  </span>
                  <span className="my-schedule__icon" aria-hidden="true">{getSportEmoji(item.sport)}</span>
                  <span className="my-schedule__what">
                    <span className="my-schedule__name">{item.title}</span>
                  </span>
                  {role && <span className={`my-schedule__role my-schedule__role--${item.role}`}>{role}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      ) : groups.length === 0 && inProgress.length > 0 && !full ? null : groups.length === 0 ? (
        <div className="my-schedule__empty">
          <p>Nothing scheduled in the next {span}</p>
          <div className="my-schedule__empty-links">
            <button type="button" onClick={() => navigate('/leagues')}>Find a league</button>
            <button type="button" onClick={() => navigate('/drop-in')}>Join a drop-in</button>
          </div>
        </div>
      ) : (
        groups.map((group) => (
          <div key={group.key} className="my-schedule__day">
            <h3 className="my-schedule__day-label">{group.label}</h3>
            <ul className="my-schedule__list">
              {group.items.map((item) => {
                const role = roleLabel(item.role);
                const meta = metaLine(item);
                return (
                  <li key={`${item.kind}-${item.id}`}>
                    <button type="button" className="my-schedule__row" onClick={() => navigate(item.link)}>
                      <span className="my-schedule__time">
                        {item.live ? <span className="my-schedule__live">LIVE</span> : timeLabel(item.startsAt, item.timeZone)}
                      </span>
                      <span className="my-schedule__icon" aria-hidden="true">{getSportEmoji(item.sport)}</span>
                      <span className="my-schedule__what">
                        <span className="my-schedule__name">{item.title}</span>
                        {meta && <span className="my-schedule__meta">{meta}</span>}
                      </span>
                      {role && <span className={`my-schedule__role my-schedule__role--${item.role}`}>{role}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      )}

      {inProgress.length > 0 && (
        <div className="my-schedule__in-progress">
          {full && <h3 className="my-schedule__day-label">My leagues &amp; tournaments</h3>}
          <ul className="my-schedule__list">
            {inProgress.map((entry) => (
              <li key={`${entry.kind}-${entry.id}`}>
                <button type="button" className="my-schedule__row my-schedule__row--compact" onClick={() => navigate(entry.link)}>
                  <span className="my-schedule__icon" aria-hidden="true">{getSportEmoji(entry.sport)}</span>
                  <span className="my-schedule__name">{inProgressLine(entry)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

    </section>
  );
};

export default MySchedule;
